import { types as t, flow, getSnapshot, getParent, Instance, getRoot, getEnv } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import Auditable from './auditable'
import ref, { safeReference } from './reference'
import { v4 as uuid } from 'uuid'
import { AppInfo } from './app'
import fetch from '../../utils/fetch'
import Assignment from './assignment'
import Mapping from './mapping'
import { withoutPatch } from '../../utils/without-patch'
import type { IStore } from '../store'
import { toJS } from 'mobx'

const SitePage = t
  .model('SitePage', {
    pathname: t.string,
    build: t.frozen()
  })
  .actions(crudActions)
const SiteConfig = t.model('SiteConfig', {
  pages: t.array(SitePage)
})
const _SiteVersion = t.model('SiteVersion', {
  id: t.identifier,
  url: t.maybeNull(t.string),
  config: t.maybeNull(SiteConfig),
  build: t.frozen()
})
  .actions(crudActions)
  .actions(self => ({
    fetchDetails() {
      const parent = getParent(self, 2) as Instance<typeof Site>
      return fetch(`/admin/site/${parent.id}/version/${self.id}`).then(r => r.json()).then(version => {
        withoutPatch(self, () => {
          self.update(version)
        })
      })
    },
    promote() {
      const parent = getParent(self, 2) as Instance<typeof Site>
      parent.setCurrentVersion(self.id)
      return fetch(`/admin/site/${parent.id}/promote/${self.id}`, { method: 'POST' }).then(r => r.json())
    }
  }))

export const SiteVersion = t.compose('SiteVersion', _SiteVersion, Auditable)

const DEFAULT_MAPPING = {
  key: 'config', type: 'object', children: [{
    key: 'pages', type: 'list', children: [{
      key: '0', type: 'object', children: [
	{ key: 'pathname', type: 'string' },
	{ key: 'title', type: 'string' },
	{ key: 'redirectTo', type: 'string' }
      ]
    }]
  }]
}			     

const Site = t
  .model('Site', {
    id: t.identifier,
    name: t.optional(t.string, ''),
    config: t.maybeNull(Mapping),
    app: ref(AppInfo, 'apps'),
    assignment: ref(Assignment, 'assignments'),
    versions: t.map(SiteVersion),
    currentVersion: safeReference(SiteVersion),
    state: t.optional(t.frozen(), {})
  })
  .views(self => ({
    get assignmentId() {
      return getSnapshot(self).assignment
    },
    getOrLoadObject (name: string, key: string) {
      const env = getEnv(self)
      if(env.getOrLoadObject) return env.getOrLoadObject(name, key)
      const root = getRoot(self) as IStore;
      if(name === 'apps') return root.apps.get(key)
      return root?.app?.get(self.app?.id)?.getOrLoadObject?.(name, key)
    },    
  }))
  .actions(crudActions)
  .actions(self => ({
    loadVersions() {
      return fetch(`/admin/site/${self.id}/versions`).then(r => r.json()).then(versions => {
        withoutPatch(self, () => {
          self.update('versions', versions)
        })
      })
    },
    deploy() {
      return fetch(`/admin/site/${self.id}/deploy`, { method: 'POST' }).then(r => r.json())
    },
    setCurrentVersion(versionId) {
      self.currentVersion = versionId
    },
    afterCreate() {
      if(!self.config) self.update('config', DEFAULT_MAPPING)
    },
    deleteDeployedVersions() {
      return fetch(`/admin/site/${self.id}/versions`, { method: 'DELETE' }).then(r => r.json());
    },
    addVersion(version: Instance<typeof SiteVersion>) {
      return withoutPatch(self, () => {
        self.versions.put({
          ...getSnapshot(version)
        })
      })
    }
  }))

export default t.compose(
  'Site',
  Site,
  Auditable
)
